<template>
  <v-dialog
    v-model="dialog"
    max-width="700px"
    :fullscreen="$vuetify.breakpoint.mdAndDown || dialogFullScreen"
    :hide-overlay="$vuetify.breakpoint.mdAndDown"
    scrollable
    no-click-animation
  >
    <v-card>
      <!-- En-tête (Toolbar primaire uniforme avec PreferencesDialog) -->
      <v-toolbar dark color="primary" :max-height="$vuetify.breakpoint.mdAndUp ? 64 : 56" class="flex-grow-0">
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>Mes permissions</v-toolbar-title>
        
        <v-progress-linear
          :active="saving || loading"
          :indeterminate="saving || loading"
          absolute
          bottom
          color="lime"
        ></v-progress-linear>
        
        <v-spacer />
        
        <v-toolbar-items>
          <v-btn v-if="$vuetify.breakpoint.mdAndUp" icon @click="dialogFullScreen = !dialogFullScreen">
            <v-icon v-if="dialogFullScreen === true">mdi-fullscreen-exit</v-icon>
            <v-icon v-if="dialogFullScreen === false">mdi-fullscreen</v-icon>
          </v-btn>
          <v-btn dark text :disabled="saving || loading" @click="savePermissions">
            <v-icon>mdi-content-save-outline</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-card-text class="pa-4">
        <!-- Chargement -->
        <div v-if="loading" class="d-flex justify-center align-center py-8">
          <v-progress-circular indeterminate color="primary" size="36" />
        </div>

        <!-- Erreur -->
        <v-alert v-else-if="loadError" type="error" dense class="mb-4">
          {{ loadError }}
        </v-alert>

        <!-- Contenu principal -->
        <template v-else>

          <!-- Section : Information rôle(s) -->
          <div class="text-subtitle-2 mb-2" style="color:#489FBB">
            {{ userRoles.length > 1 ? 'Rôles' : 'Rôle' }} :
            <span>
              <template v-for="(role, roleIndex) in userRoles">
                {{ roleLabels[role] || role }}{{ userRoles.length > 1 && roleIndex < userRoles.length - 1 ? ' / ' : '' }}
              </template>
            </span>
          </div>

          <p class="text-caption mb-4">
            Activez ou désactivez les permissions accordées par votre rôle.
          </p>

          <!-- Feedback sauvegarde en cas d'erreur -->
          <v-alert
            v-if="saveStatus.message && saveStatus.type === 'error'"
            type="error"
            dense
            outlined
            class="mb-4"
            dismissible
            @input="saveStatus.message = ''"
          >
            {{ saveStatus.message }}
          </v-alert>

          <!-- Groupes de permissions -->
          <div
            v-for="(groupPerms, groupName) in orderedPermissionsByGroup"
            v-if="visiblePermsByGroup[groupName] && visiblePermsByGroup[groupName].length > 0"
            :key="groupName"
            class="mb-4"
          >
            <v-card outlined>
              <!-- En-tête de groupe de permission (dépliable/repliable) -->
              <v-card-title
                class="subtitle-2 font-weight-bold py-2 px-3 d-flex align-center"
                style="cursor: pointer;"
                @click="toggleGroup(groupName)"
              >
                <v-btn icon x-small class="mr-1 grey--text text--darken-1" @click.stop="toggleGroup(groupName)">
                  <v-icon small>
                    {{ isGroupExpanded(groupName) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                  </v-icon>
                </v-btn>
                <span>{{ groupLabels[groupName] || groupName }}</span>
              </v-card-title>

              <!-- Liste des permissions du groupe -->
              <template v-if="isGroupExpanded(groupName)">
                <v-divider />
                <v-list dense class="pa-0">
                  <v-list-item
                    v-for="(perm, index) in visiblePermsByGroup[groupName]"
                    :key="perm.name"
                    class="px-3"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="d-flex align-center flex-wrap">
                        <span class="text-body-2 font-weight-medium">{{ permissionLabels[perm.name] || perm.name }}</span>
                        
                        <!-- Tooltip pour menu.recueil_avis -->
                        <v-tooltip v-if="perm.name === 'menu.recueil_avis'" right max-width="300px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                            >
                              mdi-help-circle-outline
                            </v-icon>
                          </template>
                          <span>S'affiche uniquement si la permission est activée ET qu'au moins un canal de recueil d'avis est configuré et actif.</span>
                        </v-tooltip>
                        
                        <!-- Badge surcharge (uniquement en cas de surcharge directe) -->
                        <v-chip
                          v-if="perm.source === 'override'"
                          x-small
                          class="ml-2 font-weight-bold"
                          color="orange lighten-4"
                          text-color="orange darken-3"
                        >
                          <v-icon x-small left>mdi-account-key-outline</v-icon>
                          Surcharge
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="isAdmin" class="text-caption grey--text pt-1">
                        {{ perm.name }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action class="my-0">
                      <v-switch
                        :id="`user-perm-switch-${perm.name.replace(/\./g, '-')}`"
                        v-model="localToggles[perm.name]"
                        :disabled="saving || (perm.name === 'rbac.manage' && !localToggles['settings.edit']) || (perm.name === 'ratings.categorized_by' && !localToggles['ratings.categorize'])"
                        color="primary"
                        dense
                        hide-details
                        class="mt-0"
                        @change="onUserToggleChange(perm.name)"
                      />
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </template>
            </v-card>
          </div>

        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>  

<script>
import PermissionsRepository from '@/repositories/PermissionsRepository'
import AuthLogic from '@/logics/AuthLogic'
import { EventBus } from '@/services/Request'
import _ from 'lodash'

const PERMISSION_ORDER = [
  // accès
  'menu.recueil_avis',
  'menu.flux_avis',
  'menu.dashboards',

  // avis_actions
  'ratings.view_categorization',
  'ratings.categorize',
  'ratings.categorized_by',
  'ratings.liz_predict',
  'ratings.view_answers',
  'questions.view_answers',
  'ratings.answer',
  'ratings.interesting',
  'ratings.flag',
  'ratings.report',
  'ratings.admin_filter',
  'ratings.share_link',
  'ratings.export',
  'ratings.summarize',
  'ratings.depollution',
  'ratings.translate',

  // compte
  'account.exports',

  // admin_global
  'users.manage',
  'units.manage',
  'semantic_categories.manage',
  'primary_categories.manage',
  'reports.manage',
  'apps.manage',
  'channels.create',
  'channels.edit',
  'channels.delete',
  'settings.edit',
  'rbac.manage',
]

export default {
  name: 'UserPermissionsPanel',

  data: () => ({
    dialog: false,
    dialogFullScreen: false,
    loading: false,
    loadError: null,
    saving: false,

    saveStatus: {
      message: '',
      type: 'success',
    },

    /**
     * Permissions groupées reçues de l'API.
     * @type {Object}
     */
    permissionsByGroup: {},

    /**
     * Toutes les permissions effectives de l'utilisateur.
     * @type {string[]}
     */
    allActivePermissions: [],

    /**
     * État local des toggles.
     * Structure : { [permissionName]: boolean }
     */
    localToggles: {},

    /**
     * État d'extension des groupes de permissions (plié/déplié).
     * Structure : { [groupName]: boolean }
     */
    expandedGroups: {},

    /** Couleurs par rôle */
    roleColors: {
      super_admin:     'purple darken-4',
      admin:           'deep-purple darken-2',
      analyste_expert: 'indigo darken-1',
      moderator:       'indigo darken-1',
      relation_client: 'teal darken-1',
      quality:         'teal darken-1',
      analyste:        'green darken-1',
      member:          'green darken-1',
      reader:          'blue-grey darken-1',
    },

    /** Labels par rôle */
    roleLabels: {
      super_admin:     'Super administrateur',
      admin:           'Administrateur',
      analyste_expert: 'Analyste expert',
      moderator:       'Analyste expert',
      relation_client: 'Relation Client',
      quality:         'Relation Client',
      analyste:        'Analyste',
      member:          'Analyste',
      reader:          'Lecteur',
    },

    /** Icônes par rôle */
    roleIcons: {
      super_admin:     'mdi-shield-crown-outline',
      admin:           'mdi-shield-crown-outline',
      analyste_expert: 'mdi-shield-star-outline',
      moderator:       'mdi-shield-star-outline',
      relation_client: 'mdi-shield-check-outline',
      quality:         'mdi-shield-check-outline',
      analyste:        'mdi-account-outline',
      member:          'mdi-account-outline',
      reader:          'mdi-eye-outline',
    },

    /** Icônes de groupe de permissions */
    groupIcons: {
      accès:         'mdi-eye-outline',
      avis_actions:  'mdi-star-outline',
      compte:        'mdi-account-circle-outline',
      admin_global:  'mdi-cog-outline',
    },

    /** Labels de groupe de permissions */
    groupLabels: {
      accès:         'Menu',
      avis_actions:  'Actions sur les avis',
      compte:        'Mon Compte',
      admin_global:  'Back Office',
    },

    /** Couleurs de groupe de permissions */
    groupColors: {
      accès:         'blue',
      avis_actions:  'orange',
      compte:        'teal',
      admin_global:  'deep-purple',
    },

    /** Labels par permission */
    permissionLabels: {
      // --- Accès ---
      'units.view_own':       'Accès à ses propres unités',
      'menu.recueil_avis':    'Recueillir des avis',
      'menu.flux_avis':       'Afficher le flux d\'avis',
      'menu.dashboards':      'Afficher les tableaux de bord',

      // --- Actions Avis ---
      'ratings.interesting':  'Marquer un avis comme intéressant',
      'ratings.flag':         'Marquer un avis comme inapproprié',
      'ratings.report':       'Signaler une erreur à Civiliz',
      'ratings.share_link':   'Partager sélection d\'avis par lien',
      'ratings.export':       'Exporter sélection d\'avis XLS',
      'ratings.summarize':    'Générer un résumé d\'une sélection d\'avis',
      'ratings.depollution':  'Nettoyer la catégorisation',
      'ratings.translate':    'Traduire une sélection d\'avis',
      'ratings.admin_filter': 'Voir filtre administration',
      'ratings.view_answers': 'Afficher les réponses aux avis',
      'questions.view_answers': 'Afficher les réponses aux questions',
      'ratings.categorize':     'Catégoriser les avis',
      'ratings.categorized_by': 'Marquer comme catégorisé par l\'utilisateur',
      'ratings.liz_predict':    'Relancer la catégorisation automatique',
      'ratings.answer':       'Répondre aux avis',
      'ratings.view_categorization': 'Afficher les étiquettes de catégories',

      // --- Mon Compte ---
      'account.organization': 'Mon compte > Mon organisation',
      'account.indicators':   'Mon compte > Mes indicateurs',
      'account.preferences':  'Mon compte > Mes préférences',
      'account.exports':      'Exporter mes données',

      // --- Administration globale ---
      'users.manage':         'Gérer les utilisateurs',
      'settings.edit':        'Accéder aux paramètres',
      'units.manage':         'Gérer les établissements (lieux)',
      'apps.manage':          'Gérer les applications',
      'reports.manage':       'Gérer les rapports',
      'rbac.manage':          'Gérer la matrice RBAC (Rôles & Permissions)',
      'channels.create':      'Créer un canal',
      'channels.edit':        'Modifier un canal',
      'channels.delete':      'Supprimer un canal',
      'semantic_categories.manage': 'Gérer les catégories sémantiques',
      'primary_categories.manage':  'Gérer les activités de lieux',
    },
  }),

  computed: {
    userRoles () {
      const user = AuthLogic.user()
      if (!user || !Array.isArray(user.roles)) return []
      const roles = user.roles.map(r => (typeof r === 'string' ? r : r.name)).filter(Boolean)
      const order = ['reader', 'relation_client', 'analyste', 'analyste_expert', 'admin', 'super_admin']
      return roles.sort((a, b) => {
        let indexA = order.indexOf(a)
        let indexB = order.indexOf(b)
        if (indexA === -1) indexA = 999
        if (indexB === -1) indexB = 999
        return indexA - indexB
      })
    },
    isAdmin () {
      return this.userRoles.includes('admin') || this.userRoles.includes('super_admin')
    },
    orderedPermissionsByGroup () {
      const order = ['accès', 'avis_actions', 'compte', 'admin_global']
      const ordered = {}
      
      order.forEach(group => {
        if (this.permissionsByGroup[group]) {
          ordered[group] = this.permissionsByGroup[group]
        }
      })
      
      Object.keys(this.permissionsByGroup).forEach(group => {
        if (!ordered[group]) {
          ordered[group] = this.permissionsByGroup[group]
        }
      })
      
      return ordered
    },
    /**
     * Permissions filtrées par groupe : on n'affiche que celles accessibles via le rôle (via_role === true).
     */
    visiblePermsByGroup () {
      const result = {}
      Object.keys(this.orderedPermissionsByGroup).forEach(group => {
        const visible = (this.orderedPermissionsByGroup[group] || []).filter(p => p.via_role)
        if (visible.length > 0) {
          result[group] = visible
        }
      })
      return result
    },
  },

  methods: {
    isGroupExpanded (groupName) {
      return this.expandedGroups[groupName] !== false
    },

    toggleGroup (groupName) {
      this.$set(this.expandedGroups, groupName, !this.isGroupExpanded(groupName))
    },

    onUserToggleChange (permName) {
      if (permName === 'settings.edit' && !this.localToggles['settings.edit']) {
        this.$set(this.localToggles, 'rbac.manage', false)
      }
      if (permName === 'ratings.categorize' && !this.localToggles['ratings.categorize']) {
        this.$set(this.localToggles, 'ratings.categorized_by', false)
      }
    },

    openDialog () {
      this.dialog = true
      this.loadMyPermissions()
    },

    close () {
      this.dialog = false
      this.saveStatus = { message: '', type: 'success' }
    },

    async loadMyPermissions () {
      this.loading = true
      this.loadError = null

      try {
        const response = await PermissionsRepository.getMyPermissions()
        const data = response.data

        const rawCustomizable = data.user_customizable_permissions || []
        this.allActivePermissions = data.permissions || []

        // Hide specific permissions
        const hiddenPermissions = ['units.view_own', 'account.indicators', 'account.organization', 'account.preferences', 'roles.manage']
        const filteredCustomizable = rawCustomizable.filter(p => !hiddenPermissions.includes(p.name))

        // Grouper et trier les permissions par groupe
        const grouped = _.groupBy(filteredCustomizable, 'group')
        const sortedGrouped = {}
        Object.keys(grouped).forEach(group => {
          sortedGrouped[group] = grouped[group].sort((a, b) => {
            let idxA = PERMISSION_ORDER.indexOf(a.name)
            let idxB = PERMISSION_ORDER.indexOf(b.name)
            if (idxA === -1) idxA = 999
            if (idxB === -1) idxB = 999
            return idxA - idxB
          })
        })
        this.permissionsByGroup = sortedGrouped

        // Initialiser les toggles locaux
        const toggles = {}
        filteredCustomizable.forEach(perm => {
          toggles[perm.name] = perm.is_active
        })
        if (!toggles['settings.edit']) {
          toggles['rbac.manage'] = false
        }
        if (!toggles['ratings.categorize']) {
          toggles['ratings.categorized_by'] = false
        }
        this.localToggles = { ...toggles }

      } catch (error) {
        console.error('[UserPermissionsPanel] Erreur chargement:', error)
        this.loadError = 'Impossible de charger vos permissions. Veuillez réessayer.'
      } finally {
        this.loading = false
      }
    },

    async savePermissions () {
      if (this.saving) return

      this.saving = true
      this.saveStatus = { message: '', type: 'success' }

      try {
        const response = await PermissionsRepository.updateMyPermissions(this.localToggles)
        const data = response.data

        const rawCustomizable = data.user_customizable_permissions || []
        this.allActivePermissions = data.permissions || []

        // Hide specific permissions
        const hiddenPermissions = ['units.view_own', 'account.indicators', 'account.organization', 'account.preferences', 'roles.manage']
        const filteredCustomizable = rawCustomizable.filter(p => !hiddenPermissions.includes(p.name))

        // Mettre à jour la structure groupée et trier
        const grouped = _.groupBy(filteredCustomizable, 'group')
        const sortedGrouped = {}
        Object.keys(grouped).forEach(group => {
          sortedGrouped[group] = grouped[group].sort((a, b) => {
            let idxA = PERMISSION_ORDER.indexOf(a.name)
            let idxB = PERMISSION_ORDER.indexOf(b.name)
            if (idxA === -1) idxA = 999
            if (idxB === -1) idxB = 999
            return idxA - idxB
          })
        })
        this.permissionsByGroup = sortedGrouped

        // Reconstruire les toggles locaux
        const toggles = {}
        filteredCustomizable.forEach(perm => {
          toggles[perm.name] = perm.is_active
        })
        if (!toggles['ratings.categorize']) {
          toggles['ratings.categorized_by'] = false
        }
        this.localToggles = { ...toggles }

        // Rafraîchir les informations utilisateur de session pour synchroniser les permissions
        await AuthLogic.loadUser()

        EventBus.$emit('user-account-updated', { type: 'permissions' })
        EventBus.$emit('show-snackbar', { text: 'Permissions mises à jour', timeout: 2500 })

        // Fermer la modale directement
        this.close()

      } catch (error) {
        console.error('[UserPermissionsPanel] Erreur sauvegarde:', error)
        this.saveStatus = {
          message: 'Erreur lors de la sauvegarde des permissions.',
          type: 'error',
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
</style>
